import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { FiZap, FiX, FiCheckCircle } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { colors } from '../../theme/colors';

// HARDCODED "What's New" (2026-07-16, Firebase cost + owner decision).
// The old version read companies/{id}/featureSuggestions via onSnapshot,
// featureSuggestionsState, and a featureRequests listener on every dashboard
// mount, plus tryFeatureOnce cloud-function trials. All of that is removed —
// this widget now costs ZERO Firestore reads. It shows a fixed set of
// promotable features, hides the ones already enabled for the company, and
// remembers dismissals in localStorage only.
//
// To change the promoted features, edit HARDCODED_FEATURES below.
const HARDCODED_FEATURES = [
  {
    moduleId: 'pos_counter_sale',
    category: 'Point of Sale',
    title: 'POS Counter Sale',
    headline: 'Sell fast at the counter — barcode scanning, cashier shifts, Z-reports and offline mode.',
  },
  {
    moduleId: 'whatsapp_connect',
    category: 'WhatsApp',
    title: 'WhatsApp Integration',
    headline: 'Send invoices, receipts and payment reminders to your customers directly on WhatsApp.',
  },
  {
    moduleId: 'fixed_assets_register',
    category: 'Accounting',
    title: 'Fixed Assets',
    headline: 'Asset register with automatic FBR-compliant depreciation and disposal accounting.',
  },
  {
    moduleId: 'settings_branches',
    category: 'Multi-Branch',
    title: 'Multi-Branch',
    headline: 'Run multiple branches with per-branch documents, stock and reports under one company.',
  },
];

const DISMISS_KEY = 'amal_whatsnew_dismissed';

const readDismissed = () => {
  try { return JSON.parse(localStorage.getItem(DISMISS_KEY)) || {}; } catch { return {}; }
};

const WhatsNewWidget = () => {
  const { activeCompany, isModuleEnabled } = useAuth();
  const [dismissed, setDismissed] = useState(readDismissed);
  const [expanded, setExpanded] = useState(false);

  // Module guard FIRST — nothing below runs when the widget is off.
  if (typeof isModuleEnabled === 'function' && !isModuleEnabled('dashboard_whats_new')) {
    return null;
  }
  if (!activeCompany?.id) return null;

  return <WidgetBody
    dismissed={dismissed}
    setDismissed={setDismissed}
    expanded={expanded}
    setExpanded={setExpanded}
    activeCompany={activeCompany}
  />;
};

const WidgetBody = ({ dismissed, setDismissed, expanded, setExpanded, activeCompany }) => {
  const visible = useMemo(() => {
    const enabled = activeCompany?.enabledModules || {};
    return HARDCODED_FEATURES
      .filter(s => !dismissed[s.moduleId])
      .filter(s => enabled[s.moduleId] !== true);
  }, [dismissed, activeCompany]);

  const top = expanded ? visible : visible.slice(0, 2);

  const handleDismiss = (s) => {
    const next = { ...dismissed, [s.moduleId]: new Date().toISOString() };
    setDismissed(next);
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };

  return (
    <Card>
      <Header>
        <HeaderLeft>
          <IconWrap><FiZap size={14} /></IconWrap>
          <Title>What's New</Title>
        </HeaderLeft>
        {visible.length > 2 && (
          <ExpandBtn onClick={() => setExpanded(v => !v)}>
            {expanded ? 'Less' : `+${visible.length - 2}`}
          </ExpandBtn>
        )}
      </Header>

      {visible.length === 0 ? (
        <Empty>
          <FiCheckCircle size={16} />
          <span>You're all caught up.</span>
        </Empty>
      ) : (
        <List>
          {top.map((s) => (
            <Suggestion key={s.moduleId}>
              <SuggHeader>
                <CategoryChip>{s.category}</CategoryChip>
                <DismissBtn onClick={() => handleDismiss(s)} title="Don't show again">
                  <FiX size={12} />
                </DismissBtn>
              </SuggHeader>
              <SuggTitle>{s.title}</SuggTitle>
              <SuggHeadline title={s.headline}>{s.headline}</SuggHeadline>
              <MutedNote>Contact AMAL ERP to enable this feature</MutedNote>
            </Suggestion>
          ))}
        </List>
      )}
    </Card>
  );
};

export default WhatsNewWidget;

// ── styled ──────────────────────────────────────────────────────────────────
// Compact rail variant — sized to sit alongside Quick Actions.
const Card = styled.div`
  background: white;
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  margin-bottom: 14px;
`;
const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
`;
const HeaderLeft = styled.div`
  display: flex; align-items: center; gap: 7px;
`;
const IconWrap = styled.div`
  width: 24px; height: 24px; border-radius: 6px;
  background: ${colors.primary.main}15;
  color: ${colors.primary.main};
  display: inline-flex; align-items: center; justify-content: center;
`;
const Title = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.text.primary};
`;
const ExpandBtn = styled.button`
  font-size: 11px;
  font-weight: 700;
  background: ${colors.primary.main}10;
  border: 1px solid ${colors.primary.main}30;
  color: ${colors.primary.main};
  padding: 3px 8px;
  border-radius: 10px;
  cursor: pointer;
  &:hover { background: ${colors.primary.main}20; }
`;
const List = styled.div`
  display: flex; flex-direction: column; gap: 8px;
`;
const Suggestion = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 10px;
  background: ${colors.background?.paper || '#fafafa'};
  border: 1px solid ${colors.neutral?.[200] || '#e5e7eb'};
  border-radius: 6px;
`;
const SuggHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 6px;
`;
const CategoryChip = styled.span`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 3px;
  background: ${colors.primary.main}18;
  color: ${colors.primary.main};
`;
const SuggTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${colors.text.primary};
  line-height: 1.3;
`;
const SuggHeadline = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.text.secondary};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const MutedNote = styled.span`
  font-size: 10px;
  font-weight: 500;
  color: ${colors.text.secondary};
  font-style: italic;
`;
const DismissBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  width: 20px; height: 20px;
  background: transparent;
  border: none;
  color: ${colors.text.secondary};
  border-radius: 3px;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { color: ${colors.error.main}; background: ${colors.error.main}08; }
`;
const Empty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px 10px;
  color: ${colors.text.primary};
  font-size: 12px;
  font-weight: 500;
  text-align: center;
`;
